import React, { useState } from 'react';
import { SelectOption } from 'global';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { gql, useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { useHistory, useLocation } from 'react-router-dom';
import { metabaseApi } from 'modules/integrations/metabase/api';
import { MetabaseDashboardIds } from 'modules/integrations/metabase/types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { Box, CircularProgress, Stack } from '@mui/material';
import { MetaBaseDashboardResponse } from 'modules/integrations/metabase/api/types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

const GET_DASHBOARDS = gql`
  query dashboards {
    dashboards {
      dashboardId
      dashboardUrl
      dashboardName
      superAdminOnly
      dashboardDescription
    }
  }
`;

export const DealerDashboards = () => {
  const classes = useStyles();
  const { replace } = useHistory();
  const { pathname, search } = useLocation();

  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [refetching, setRefetching] = useState(false);
  const [options, setOptions] = useState<SelectOption[]>([]);

  const handleDashboardChange = async (value: string | null) => {
    setRefetching(true);
    setDashboardId(value);
  };

  const { error, data: dashboardsResponse } = useQuery(GET_DASHBOARDS, {
    context: { authRequired: true },
    onCompleted(res) {
      const dbOptions = (res.dashboards ?? []).map((dashboard: any) => ({
        value: dashboard.dashboardId,
        label: dashboard.dashboardName,
      }));

      setOptions(prev => [...prev, ...dbOptions]);

      if (res.dashboards.length === 1) {
        setDashboardId(res.dashboards[0].dashboardId);
      }
    },
  });

  useQuery<MetaBaseDashboardResponse, Record<'input', { dashboardId: number }>>(
    metabaseApi.metabaseDashboardIframeURL,
    {
      variables: { input: { dashboardId: MetabaseDashboardIds.Fifth } },
      onCompleted: ({ metaBaseDashboard }) => {
        if (!metaBaseDashboard?.url) return;

        setOptions(prev => [...prev, { label: 'Submissions Hub', value: metaBaseDashboard.url! }]);

        const params = new URLSearchParams(search);

        if (params.get('initialDashboardLabel') === 'submissions') {
          setDashboardId(metaBaseDashboard.url!);
          handleDashboardChange(metaBaseDashboard.url);
          replace({ pathname });
        }
      },
    }
  );

  if (error) return <h3>{String(error)}</h3>;

  return (
    <CardContainer>
      <Box className={classes.dashboardContainer}>
        <FormControl fullWidth>
          <InputLabel variant="standard" htmlFor="simple-select" shrink>
            Select Dashboard
          </InputLabel>

          <Select value={dashboardId ?? null} onChange={({ target }) => handleDashboardChange(target.value)}>
            {options.map(({ value, label }) => (
              <MenuItem value={value} key={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <RenderWithCondition condition={refetching}>
          <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '79vh' }}>
            <CircularProgress />
          </Stack>
        </RenderWithCondition>

        {dashboardId && (
          <iframe
            width="100%"
            frameBorder="0"
            style={{ minHeight: '79vh', display: refetching ? 'none' : 'inherit' }}
            src={
              dashboardId.startsWith('http')
                ? dashboardId
                : dashboardsResponse.dashboards.find((db: any) => db.dashboardId === dashboardId).dashboardUrl
            }
            onLoad={() => {
              setTimeout(() => {
                setRefetching(false);
              }, 2000);
            }}
          />
        )}
      </Box>
    </CardContainer>
  );
};

const useStyles = makeStyles({
  dashboardContainer: {
    gap: '1rem',
    display: 'flex',
    minHeight: '80vh',
    flexDirection: 'column',
  },
});
